import React, { useState, useEffect } from 'react';
import { useAuth, API } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Plus, Megaphone, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import ConfirmModal from '../components/ConfirmModal';
import axios from 'axios';
import { toast } from 'sonner';
import { trackModuleVisit, trackEvent } from '@/lib/posthog';

const AnnouncementsPage = () => {
  const { user, selectedBuilding } = useAuth();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [buildings, setBuildings] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [announcementToDelete, setAnnouncementToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [formData, setFormData] = useState({
    building_id: '',
    title: '',
    content: '',
    urgency: 'normal'
  });

  // Track module visit on mount
  useEffect(() => {
    trackModuleVisit('avisos');
  }, []);

  useEffect(() => {
    fetchBuildings();
  }, []);

  useEffect(() => {
    fetchAnnouncements();
  }, [selectedBuilding]);

  const fetchBuildings = async () => {
    try {
      const response = await axios.get(`${API}/buildings`);
      setBuildings(response.data);
      const defaultBuilding = (selectedBuilding && selectedBuilding !== 'all') 
        ? selectedBuilding 
        : (response.data.length > 0 ? response.data[0].id : '');
      setFormData(prev => ({ ...prev, building_id: defaultBuilding }));
    } catch (error) {
      console.error('Error fetching buildings:', error);
    }
  };

  const fetchAnnouncements = async () => {
    setLoading(true);
    try {
      const query = selectedBuilding && selectedBuilding !== 'all' ? `?building_id=${selectedBuilding}` : '';
      const response = await axios.get(`${API}/announcements${query}`);
      setAnnouncements(response.data);
    } catch (error) {
      console.error('Error fetching announcements:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/announcements`, formData);
      toast.success('Aviso publicado exitosamente');
      // Track announcement creation
      trackEvent('aviso_created', { building_id: formData.building_id, tipo: formData.urgency });
      setDialogOpen(false);
      setFormData({
        building_id: buildings[0]?.id || '',
        title: '',
        content: '',
        urgency: 'normal'
      });
      fetchAnnouncements();
    } catch (error) {
      toast.error('Error al publicar aviso');
    }
  };

  const getUrgencyColor = (urgency) => {
    const colors = {
      normal: 'bg-slate-100 text-slate-700',
      important: 'bg-blue-100 text-blue-700',
      urgent: 'bg-red-100 text-red-700'
    };
    return colors[urgency] || colors.normal;
  };

  const getUrgencyLabel = (urgency) => {
    const labels = {
      normal: 'Normal',
      important: 'Importante',
      urgent: 'Urgente'
    };
    return labels[urgency] || urgency;
  };

  const handleDeleteClick = (announcement) => {
    setAnnouncementToDelete(announcement);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!announcementToDelete) return;
    
    setDeleteLoading(true);
    try {
      await axios.delete(`${API}/announcements/${announcementToDelete.id}`);
      // Optimistic UI update
      setAnnouncements(prev => prev.filter(a => a.id !== announcementToDelete.id));
      toast.success('Aviso eliminado exitosamente');
      trackEvent('aviso_deleted', { announcement_id: announcementToDelete.id });
      setDeleteModalOpen(false);
      setAnnouncementToDelete(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar aviso');
    } finally {
      setDeleteLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-heading font-bold text-primary" data-testid="announcements-page-title">Avisos</h1>
            <p className="text-text-secondary mt-1">Comunicados y notificaciones</p>
          </div>
          {(user?.role === 'admin' || user?.role === 'super_admin') && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-accent hover:bg-accent/90 text-white rounded-full" data-testid="create-announcement-button">
                  <Plus className="h-5 w-5 mr-2" />
                  Nuevo Aviso
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="create-announcement-dialog">
                <DialogHeader>
                  <DialogTitle>Publicar nuevo aviso</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label>Conjunto</Label>
                    <Select value={formData.building_id} onValueChange={(value) => setFormData({...formData, building_id: value})}>
                      <SelectTrigger data-testid="announcement-building-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {buildings.map(building => (
                          <SelectItem key={building.id} value={building.id}>{building.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Título</Label>
                    <Input
                      value={formData.title}
                      onChange={(e) => setFormData({...formData, title: e.target.value})}
                      required
                      data-testid="announcement-title-input"
                    />
                  </div>
                  <div>
                    <Label>Contenido</Label>
                    <Textarea
                      value={formData.content}
                      onChange={(e) => setFormData({...formData, content: e.target.value})}
                      required
                      rows={4}
                      data-testid="announcement-content-input"
                    />
                  </div>
                  <div>
                    <Label>Urgencia</Label>
                    <Select value={formData.urgency} onValueChange={(value) => setFormData({...formData, urgency: value})}>
                      <SelectTrigger data-testid="announcement-urgency-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="normal">Normal</SelectItem>
                        <SelectItem value="important">Importante</SelectItem>
                        <SelectItem value="urgent">Urgente</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button type="submit" className="w-full bg-accent hover:bg-accent/90" data-testid="announcement-submit-button">
                    Publicar Aviso
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Announcements List */}
        <div className="grid gap-4">
          {announcements.length === 0 ? (
            <Card className="p-12 text-center">
              <Megaphone className="h-12 w-12 text-text-secondary mx-auto mb-4" />
              <p className="text-text-secondary">No hay avisos publicados</p>
            </Card>
          ) : (
            announcements.map((announcement) => (
              <Card key={announcement.id} className="p-6 hover:shadow-md transition-shadow" data-testid={`announcement-item-${announcement.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-xl font-semibold text-primary flex-1">{announcement.title}</h3>
                  <div className="flex items-center gap-2">
                    <Badge className={getUrgencyColor(announcement.urgency)}>
                      {getUrgencyLabel(announcement.urgency)}
                    </Badge>
                    {(user?.role === 'admin' || user?.role === 'super_admin') && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteClick(announcement)}
                        className="h-8 w-8 p-0 text-slate-400 hover:text-red-600 hover:bg-red-50"
                        data-testid={`delete-announcement-${announcement.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
                <p className="text-text-secondary mb-4 whitespace-pre-line">{announcement.content}</p>
                <div className="flex items-center gap-4 text-sm text-text-secondary">
                  <span>Publicado por: <span className="font-medium">{announcement.created_by_name}</span></span>
                  <span>Fecha: <span className="font-medium">{new Date(announcement.created_at).toLocaleDateString('es-ES')}</span></span>
                </div>
              </Card>
            ))
          )}
        </div>

        {/* Delete Confirmation Modal */}
        <ConfirmModal
          open={deleteModalOpen}
          onOpenChange={setDeleteModalOpen}
          title="Eliminar Aviso"
          description={`¿Estás seguro de eliminar "${announcementToDelete?.title}"? Esta acción no se puede deshacer.`}
          confirmText="Eliminar"
          onConfirm={handleDeleteConfirm}
          loading={deleteLoading}
          variant="danger"
        />
      </div>
    </DashboardLayout>
  );
};

export default AnnouncementsPage;